// ChatOrchestrator.js - Orchestrateur intelligent utilisant le backend

import ChatBackendService from './ChatBackendService';

/**
 * ChatOrchestrator - Orchestrateur central pour le système de chat intelligent
 * 
 * Utilise ChatBackendService pour tous les appels API
 * Le backend gère l'analyse d'intention et le routage intelligent
 */
class ChatOrchestrator {
  constructor() {
    this.backendService = ChatBackendService;
  }

  /**
   * Traite une requête utilisateur de manière intelligente
   * @param {string} query - Question de l'utilisateur
   * @param {Object} context - Contexte complet (objectif, étape, sources, etc.)
   * @param {Object} options - Options (forceTool, generateRecommendations, etc.)
   * @returns {Promise<Object>} - Résultat avec answer, source, metadata, etc.
   */
  async processQuery(query, context, options = {}) {
    const startTime = Date.now();
    
    try {
      console.log('🎯 [ChatOrchestrator] Traitement de la requête:', {
        query: query.substring(0, 200),
        options: options,
        contextSummary: this.getContextSummary(context),
        timestamp: new Date().toISOString()
      });
      
      // Préparer le contexte pour le backend
      const backendContext = this.prepareContext(context);
      
      // Si un outil est forcé, l'utiliser directement
      if (options.forceTool && options.forceTool !== 'auto') {
        console.log(`🔧 [ChatOrchestrator] Outil forcé: ${options.forceTool}`);
        return await this.executeWithForcedTool(query, backendContext, options.forceTool);
      }
      
      // Sinon, laisser le backend analyser l'intention et choisir l'outil
      console.log('🤖 [ChatOrchestrator] Mode auto: analyse d\'intention...');
      return await this.executeWithIntentAnalysis(query, backendContext, options);
      
    } catch (error) {
      console.error('❌ [ChatOrchestrator] Erreur:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      
      // Fallback: conversation simple
      return await this.fallbackToConversation(query, context, error);
    }
  }

  /**
   * Prépare le contexte pour le backend
   * ⚡ OPTIMISÉ : Exclut les notes volumineuses (resources, results) pour réduire la taille des prompts
   */
  prepareContext(context) {
    // Filtrer les notes de l'étape courante (garder uniquement decisions, questions, ai_recommendations)
    const optimizedStepNotes = context.stepNotes ? {
      decisions: context.stepNotes.decisions || '',
      questions: context.stepNotes.questions || '',
      ai_recommendations: context.stepNotes.ai_recommendations || ''
      // ❌ EXCLU : resources, results (trop volumineux)
    } : {
      decisions: '',
      questions: '',
      ai_recommendations: ''
    };

    // Filtrer les notes de toutes les étapes (même optimisation)
    const optimizedAllStepsNotes = (context.allStepsNotes || []).map(step => ({
      stepId: step.stepId,
      stepTitle: step.stepTitle,
      stepOrder: step.stepOrder,
      notes: {
        decisions: step.notes?.decisions || '',
        questions: step.notes?.questions || '',
        ai_recommendations: step.notes?.ai_recommendations || ''
        // ❌ EXCLU : resources, results (trop volumineux)
      },
      isCurrentStep: step.isCurrentStep,
      isCompleted: step.isCompleted
    }));

    const preparedContext = {
      userId: context.userId,
      competenceId: context.competenceId,
      objective: context.objective ? {
        id: context.objective.id || context.competenceId, // Ajout de l'ID pour le backend
        titre: context.objective.titre,
        description: context.objective.description
      } : null,
      strategy: context.strategy || null,
      actionPlan: context.actionPlan ? {
        titre: context.actionPlan.titre,
        steps: context.actionPlan.steps
      } : null,
      currentStep: context.currentStep ? {
        id: context.currentStep.id,
        titre: context.currentStep.titre,
        description: context.currentStep.description
      } : null,
      selectedSources: context.selectedSources || [],
      notes: context.notes || '',
      stepNotes: optimizedStepNotes,
      allStepsNotes: optimizedAllStepsNotes,
      confirmedAdvancements: context.confirmedAdvancements || [],
      conversationHistory: context.conversationHistory || []
    };

    console.log('📋 [ChatOrchestrator] Contexte préparé:', {
      objective: preparedContext.objective?.titre,
      currentStep: preparedContext.currentStep?.titre,
      selectedSourcesCount: preparedContext.selectedSources?.length || 0,
      conversationHistoryLength: preparedContext.conversationHistory?.length || 0,
      stepNotes: preparedContext.stepNotes ? 'présent' : 'absent',
      allStepsNotesCount: preparedContext.allStepsNotes?.length || 0
    });

    return preparedContext;
  }

  /**
   * Exécute avec un outil forcé par l'utilisateur
   */
  async executeWithForcedTool(query, context, toolName) {
    const startTime = Date.now();
    
    try {
      console.log(`⚙️ [ChatOrchestrator] Exécution avec outil forcé: ${toolName}`, {
        query: query.substring(0, 100),
        selectedSourcesCount: context.selectedSources?.length || 0
      });
      
      let result;
      
      switch (toolName) {
        case 'web_search': // ✅ NOUVEAU : Accepter 'web_search' du dropdown
        case 'perplexity_sonar':
          console.log('🔎 [ChatOrchestrator] Utilisation de Perplexity Sonar (recherche web)...');
          result = await this.backendService.perplexityWebSearch(query, context);
          return this.formatResponse(result, 'perplexity_sonar', 'Perplexity Sonar', startTime);
          
        case 'gemini_search':
          console.log('🌐 [ChatOrchestrator] Utilisation de Gemini Search...');
          result = await this.backendService.geminiWebSearch(query, context);
          return this.formatResponse(result, 'gemini_search', 'Gemini Search', startTime);
          
        case 'perplexity_analysis':
        case 'source_analysis': // Alias pour compatibilité avec l'interface
          if (!context.selectedSources || context.selectedSources.length === 0) {
            console.error('❌ [ChatOrchestrator] Aucune source sélectionnée pour l\'analyse');
            throw new Error('Aucune source sélectionnée pour l\'analyse');
          }
          console.log(`📊 [ChatOrchestrator] Analyse de ${context.selectedSources.length} sources avec Perplexity...`);
          result = await this.backendService.perplexityAnalyzeSources(
            context.selectedSources,
            query,
            context
          );
          return this.formatResponse(result, 'perplexity_analysis', 'Perplexity Analysis', startTime);
          
        case 'knowledge_base':
          console.log('📚 [ChatOrchestrator] Recherche dans la base de connaissances...');
          result = await this.backendService.searchKnowledgeBase(query, context);
          return this.formatResponse(result, 'knowledge_base', 'Base de Connaissances', startTime);
          
        case 'gemini_flash':
        default:
          console.log('💬 [ChatOrchestrator] Conversation avec Gemini Flash...');
          result = await this.backendService.geminiConversation(
            query,
            context,
            context.conversationHistory,
            { temperature: 0.7 }
          );
          return this.formatResponse(result, 'gemini_flash', 'Gemini Flash', startTime);
      }
    } catch (error) {
      console.error(`❌ [ChatOrchestrator] Erreur avec ${toolName}:`, {
        message: error.message,
        tool: toolName,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  /**
   * Exécute avec analyse d'intention automatique
   */
  async executeWithIntentAnalysis(query, context, options) {
    const startTime = Date.now();
    
    try {
      // 1. Analyser l'intention
      console.log('🔍 [ChatOrchestrator] Analyse d\'intention en cours...');
      const intent = await this.backendService.analyzeIntent(query, context);
      
      console.log('✅ [ChatOrchestrator] Intention détectée:', {
        type: intent.queryType,
        confidence: intent.confidence,
        isRelevant: intent.isRelevant,
        suggestedAction: intent.suggestedAction,
        timestamp: new Date().toISOString()
      });
      
      // 2. Vérifier la pertinence
      if (!intent.isRelevant && intent.queryType === 'clarification') {
        console.log('❓ [ChatOrchestrator] Clarification nécessaire');
        const clarificationMessage = intent.clarificationQuestion || 
                  intent.suggestedAction || 
                  'Pourriez-vous préciser votre question ou votre besoin ? Je suis là pour vous aider ! 😊\n\n' +
                  'Par exemple, vous pouvez me demander de :\n' +
                  '• Rechercher des informations sur le web\n' +
                  '• Analyser vos sources sélectionnées\n' +
                  '• Chercher dans votre base de connaissances\n' +
                  '• Discuter de votre objectif ou de votre étape actuelle';
        
        return {
          answer: clarificationMessage,
          source: 'clarification',
          sourceLabel: 'Clarification Nécessaire',
          intent: intent,
          processingTime: Date.now() - startTime
        };
      }
      
      // 3. Router vers le bon outil selon l'intention
      let result;
      
      switch (intent.queryType) {
        case 'web_search':
          console.log('🔎 [ChatOrchestrator] Routage vers Perplexity Sonar (recherche web)...');
          // ✅ NOUVEAU : Utiliser Perplexity Sonar pour toutes les recherches web
          result = await this.backendService.perplexityWebSearch(query, context);
          return this.formatResponse(result, 'perplexity_sonar', 'Perplexity Sonar', startTime, intent);
          
        case 'source_analysis':
          if (context.selectedSources && context.selectedSources.length > 0) {
            console.log(`📊 [ChatOrchestrator] Routage vers Perplexity Analysis (${context.selectedSources.length} sources)...`);
            result = await this.backendService.perplexityAnalyzeSources(
              context.selectedSources,
              query,
              context
            );
            return this.formatResponse(result, 'perplexity_analysis', 'Perplexity Analysis', startTime, intent);
          } else {
            console.log('⚠️ [ChatOrchestrator] Aucune source sélectionnée, fallback vers conversation...');
            // Fallback: conversation
            result = await this.backendService.geminiConversation(query, context, context.conversationHistory);
            return this.formatResponse(result, 'gemini_flash', 'Gemini Flash', startTime, intent);
          }
          
        case 'knowledge_base':
          console.log('📚 [ChatOrchestrator] Routage vers Base de Connaissances...');
          result = await this.backendService.searchKnowledgeBase(query, context);
          return this.formatResponse(result, 'knowledge_base', 'Base de Connaissances', startTime, intent);
          
        case 'conversation':
        default:
          // ✅ MODIFICATION : Utiliser Perplexity Sonar par défaut au lieu de Gemini
          console.log('🔎 [ChatOrchestrator] Routage vers Perplexity Sonar (mode auto par défaut)...');
          result = await this.backendService.perplexityWebSearch(query, context);
          return this.formatResponse(result, 'perplexity_sonar', 'Perplexity Sonar', startTime, intent);
      }
      
    } catch (error) {
      console.error('❌ [ChatOrchestrator] Erreur analyse intention:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      
      // ✅ MODIFICATION : Fallback vers Perplexity au lieu de Gemini
      console.log('⚠️ [ChatOrchestrator] Fallback vers Perplexity Sonar...');
      const result = await this.backendService.perplexityWebSearch(query, context);
      return this.formatResponse(result, 'perplexity_sonar', 'Perplexity Sonar (Fallback)', startTime);
    }
  }

  /**
   * Fallback en cas d'erreur: conversation simple
   */
  async fallbackToConversation(query, context, originalError) {
    const startTime = Date.now();
    
    try {
      console.log('⚠️ [ChatOrchestrator] Utilisation du fallback suite à une erreur...', {
        originalError: originalError.message,
        timestamp: new Date().toISOString()
      });
      
      const result = await this.backendService.geminiConversation(
        query,
        this.prepareContext(context),
        context.conversationHistory || [],
        { temperature: 0.7 }
      );
      
      return {
        answer: result.answer,
        source: 'gemini_flash',
        sourceLabel: 'Gemini Flash (Fallback)',
        model: 'gemini-2.0-flash-exp',
        usage: result.usage,
        processingTime: Date.now() - startTime,
        fallback: true,
        originalError: originalError.message
      };
      
    } catch (fallbackError) {
      console.error('❌ [ChatOrchestrator] Échec du fallback:', {
        fallbackError: fallbackError.message,
        originalError: originalError.message,
        timestamp: new Date().toISOString()
      });
      
      return {
        answer: `Désolé, une erreur s'est produite lors du traitement de votre demande : ${originalError.message}\n\nJe vais essayer de répondre avec mes connaissances générales.`,
        source: 'error',
        sourceLabel: 'Erreur',
        processingTime: Date.now() - startTime,
        error: true,
        errorMessage: originalError.message
      };
    }
  }

  /**
   * Formate la réponse de manière uniforme
   */
  formatResponse(result, source, sourceLabel, startTime, intent = null) {
    const response = {
      answer: result.answer,
      source: source,
      sourceLabel: sourceLabel,
      model: this.getModelName(source),
      usage: result.usage,
      cost: this.estimateCost(source, result.usage),
      citations: result.citations || [],
      relatedQuestions: result.relatedQuestions || [], // ✨ NOUVEAU : Questions connexes de Perplexity
      documents: result.documents || [],
      synthesis: result.synthesis || null,
      processingTime: Date.now() - startTime,
      intent: intent,
      quota: result.quota
    };

    console.log('✅ [ChatOrchestrator] Réponse formatée:', {
      source: source,
      sourceLabel: sourceLabel,
      answerLength: result.answer?.length || 0,
      citationsCount: result.citations?.length || 0,
      relatedQuestionsCount: result.relatedQuestions?.length || 0, // ✨ NOUVEAU
      documentsCount: result.documents?.length || 0,
      processingTime: response.processingTime,
      cost: response.cost?.amount,
      timestamp: new Date().toISOString()
    });

    return response;
  }

  /**
   * Retourne le nom du modèle utilisé
   */
  getModelName(source) {
    const modelMap = {
      'gemini_search': 'gemini-2.0-flash-exp',
      'gemini_flash': 'gemini-2.0-flash-exp',
      'perplexity_sonar': 'sonar',
      'perplexity_analysis': 'sonar',
      'knowledge_base': 'gemini-2.0-flash-exp',
      'gpt4o_mini': 'gpt-4o-mini'
    };
    return modelMap[source] || 'unknown';
  }

  /**
   * Estime le coût de la requête
   */
  estimateCost(source, usage) {
    if (!usage) return null;
    
    const costPerToken = {
      'gemini_search': 0, // Gratuit (1500/jour)
      'gemini_flash': 0.000000075, // $0.075 / 1M tokens
      'perplexity_sonar': 0.000005, // $5 / 1M tokens
      'perplexity_analysis': 0.000005,
      'knowledge_base': 0.000000075,
      'gpt4o_mini': 0.00000015 // $0.15 / 1M tokens input
    };
    
    const rate = costPerToken[source] || 0;
    const totalTokens = (usage.inputTokens || 0) + (usage.outputTokens || 0);
    
    return {
      amount: totalTokens * rate,
      currency: 'USD',
      tokens: totalTokens
    };
  }

  /**
   * Génère un résumé du contexte pour les logs
   */
  getContextSummary(context) {
    return {
      objective: context.objective?.titre || 'Aucun',
      currentStep: context.currentStep?.titre || 'Aucune',
      selectedSourcesCount: context.selectedSources?.length || 0,
      conversationHistoryLength: context.conversationHistory?.length || 0,
      hasStepNotes: !!context.stepNotes,
      allStepsNotesCount: context.allStepsNotes?.length || 0
    };
  }
}

export default ChatOrchestrator;
